import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { fetchFacilitiesForFilters, fetchSportsForFilters, selectFacilities, selectSports } from '../../redux/slices/pitchFiltersSlice';
import { fetchPitches, selectAllPitches } from '../../redux/viewPitchSlice';
import { fetchBookingDetails, clearBookingDetails, resetError, setFilters, setCurrentPage } from '../../redux/bookingDetailsSlice';
import { getContainerClass } from '../../components/navigation/navigation';
// import Header from '../../components/header/header';
// import Cover from '../../components/cover/cover';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './AnalyticsPage.css';
import { getAllBookingDetails } from '../../services/api';

// Skeleton components for loading states
const SkeletonTable = ({ title }) => (
  <Card>
    <Card.Header className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">{title}</h5>
      <div className="d-flex gap-2">
        <div className="skeleton skeleton-button"></div>
        <div className="skeleton skeleton-button"></div>
      </div>
    </Card.Header>
    <Card.Body>
      <div className="skeleton-table">
        <div className="skeleton skeleton-table-header mb-3"></div>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="skeleton skeleton-table-row mb-2"></div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

const SkeletonFilters = () => (
  <Card className="filter-card mb-4">
    <Card.Body>
      <Row className="g-3 align-items-end">
        <Col md={3}>
          <Form.Label>Time Period</Form.Label>
          <div className="skeleton skeleton-select"></div>
        </Col>
        <Col md={3}>
          <Form.Label>Facility</Form.Label>
          <div className="skeleton skeleton-select"></div>
        </Col>
        <Col md={3}>
          <Form.Label>Sport</Form.Label>
          <div className="skeleton skeleton-select"></div>
        </Col>
        <Col md={3}>
          <Form.Label>Pitch/Court</Form.Label>
          <div className="skeleton skeleton-select"></div>
        </Col>
        <Col md={3}>
          <div className="d-flex gap-2">
            <div className="skeleton skeleton-button flex-grow-1"></div>
            <div className="skeleton skeleton-button" style={{ minWidth: '90px' }}></div>
            <div className="skeleton skeleton-button" style={{ minWidth: '70px' }}></div>
          </div>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);



const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // const breadcrumb = ["Home", "Reports"];

  const {
    data: bookingDetails,
    loading,
    error,
    filters,
    pagination
  } = useSelector(state => state.bookingDetails);

  // Get facilities and sports from pitchFiltersSlice
  const facilitiesFromFilters = useSelector(selectFacilities);
  const sports = useSelector(selectSports);

  const [selectedFacility, setSelectedFacility] = useState('');
  const [filteredSports, setFilteredSports] = useState([]);
  const [filteredPitches, setFilteredPitches] = useState([]);
  const [availablePitches, setAvailablePitches] = useState([]);
  const [hasApplied, setHasApplied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);


  // Period options
  const periodOptions = [
    // { value: '1month', label: '1 Month' },
    // { value: '3months', label: '3 Months' },
    // { value: '6months', label: '6 Months' },
    { value: '1year', label: '1 Year' },
    { value: 'custom', label: 'Custom' }
  ];

  const toMMDDYYYY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${m}-${d}-${y}`;
  };

  const clean = (obj) => {
    const base = {
      period: obj.period,
      facility: obj.facility || '',
      sport: obj.sport || '',
      pitch: obj.pitch || ''
    };
    if (obj.period === 'custom' && obj.from_date && obj.to_date) {
      base.startDate = toMMDDYYYY(obj.from_date);
      base.endDate = toMMDDYYYY(obj.to_date);
    }
    return base;
  };

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facilitiesRes, sportsRes, pitchesRes] = await Promise.all([
          dispatch(fetchFacilitiesForFilters()),
          dispatch(fetchSportsForFilters()),
          dispatch(fetchPitches())
        ]);

        if (pitchesRes?.payload) {
          setAvailablePitches(pitchesRes.payload);
          console.log('Available Pitches Set:', pitchesRes.payload);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
    // Load initial booking details with default filters
    const defaultFilters = { period: '1year', facility: '', sport: '', pitch: '', from_date: '', to_date: '' };
    dispatch(fetchBookingDetails({ filters: clean(defaultFilters), pagination: { page: 1, limit: 10 } }));
    setHasApplied(true); // treat initial load as applied so subsequent changes auto-apply
  }, [dispatch]);

  // Get filtered sports based on selected facility
  const getFilteredSports = () => {
    const currentFacility = filters.facility || selectedFacility;
    if (!currentFacility || !facilitiesFromFilters || !sports) return [];
    const selectedFacilityData = facilitiesFromFilters.find(f => f._id === currentFacility);
    if (!selectedFacilityData || !selectedFacilityData.availableSports) return [];

    return sports.filter(sport =>
      selectedFacilityData.availableSports.some(availableSport =>
        availableSport._id === sport._id
      )
    );
  };

  // Get filtered pitches based on selected facility and sport
  const getFilteredPitches = () => {
    const currentFacility = filters.facility || selectedFacility;
    const currentSport = filters.sport;
    
    if (!currentFacility || !currentSport) {
      console.log('Missing facility or sport selection:', { currentFacility, currentSport });
      return [];
    }

    if (!facilitiesFromFilters || !Array.isArray(facilitiesFromFilters) || facilitiesFromFilters.length === 0) {
      console.log('Facilities not loaded:', facilitiesFromFilters);
      return [];
    }

    if (!sports || !Array.isArray(sports) || sports.length === 0) {
      console.log('Sports not loaded:', sports);
      return [];
    }

    if (!availablePitches || !Array.isArray(availablePitches)) {
      console.log('Available pitches not loaded or invalid:', availablePitches);
      return [];
    }

    // Find the selected facility and sport names
    const selectedFacilityData = facilitiesFromFilters.find(f => f._id === currentFacility);
    const selectedSportData = sports.find(s => s._id === currentSport);

    if (!selectedFacilityData) {
      console.log('Facility not found:', currentFacility, 'Available facilities:', facilitiesFromFilters.map(f => ({ id: f._id, name: f.name })));
      return [];
    }

    if (!selectedSportData) {
      console.log('Sport not found:', currentSport, 'Available sports:', sports.map(s => ({ id: s._id, name: s.name })));
      return [];
    }

    if (!selectedFacilityData?.name || !selectedSportData?.name) {
      console.log('Missing facility or sport name:', { selectedFacilityData, selectedSportData });
      return [];
    }

    console.log('Filtering Pitches:', {
      selectedFacilityId: currentFacility,
      selectedFacilityName: selectedFacilityData.name,
      selectedSportId: currentSport,
      selectedSportName: selectedSportData.name,
      availablePitchesCount: availablePitches.length,
      samplePitch: availablePitches[0]
    });

    // Filter pitches based on facility name and sport name
    return availablePitches.filter(pitch => {
      if (!pitch?.facilityName || !pitch?.sportName) {
        console.log('Invalid pitch data:', pitch);
        return false;
      }

      const facilityNameMatch = String(pitch.facilityName).toLowerCase().trim() === String(selectedFacilityData.name).toLowerCase().trim();
      const sportNameMatch = String(pitch.sportName).toLowerCase().trim() === String(selectedSportData.name).toLowerCase().trim();

      console.log(`Checking Pitch ${pitch.name}:`, {
        pitchFacilityName: pitch.facilityName,
        selectedFacilityName: selectedFacilityData.name,
        facilityMatch: facilityNameMatch,
        pitchSportName: pitch.sportName,
        selectedSportName: selectedSportData.name,
        sportMatch: sportNameMatch
      });

      return facilityNameMatch && sportNameMatch;
    });
  };

  // Update sports when facility changes
  useEffect(() => {
    const currentFacility = filters.facility || selectedFacility;
    if (currentFacility) {
      const filtered = getFilteredSports();
      setFilteredSports(filtered);
    } else {
      setFilteredSports([]);
    }
  }, [filters.facility, selectedFacility, facilitiesFromFilters, sports]);

  // Update pitches when facility or sport changes
  useEffect(() => {
    const currentFacility = filters.facility || selectedFacility;
    if (currentFacility && filters.sport) {
      const filtered = getFilteredPitches();
      console.log('Filtered pitches result:', filtered);
      setFilteredPitches(filtered);
    } else {
      setFilteredPitches([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.facility, filters.sport, selectedFacility, availablePitches, facilitiesFromFilters, sports]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (key === 'facility') {
      newFilters.sport = '';
      newFilters.pitch = '';
      setSelectedFacility(value);
    } else if (key === 'sport') {
      newFilters.pitch = '';
    }
    dispatch(setFilters(newFilters));

    // Auto-apply only when NOT in custom mode
    if (hasApplied) {
      const newIsCustom = newFilters.period === 'custom';
      const prevIsCustom = filters.period === 'custom';
      if (!newIsCustom && !prevIsCustom) {
        dispatch(fetchBookingDetails({ filters: clean(newFilters), pagination: { page: 1, limit: pagination.limit } }));
      }
    }
  };

  const handleApplyFilters = () => {
    // If custom period is selected, require both dates
    if (filters.period === 'custom' && (!filters.from_date || !filters.to_date)) {
      return; // silently block; UI disables button too
    }
    dispatch(fetchBookingDetails({ filters: clean(filters), pagination: { page: 1, limit: pagination.limit } }));
    setHasApplied(true);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await dispatch(fetchBookingDetails({ filters: clean(filters), pagination: { page: pagination.currentPage, limit: pagination.limit } }));
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    dispatch(clearBookingDetails());
    setSelectedFacility('');
    setFilteredSports([]);
    setFilteredPitches([]);
    const defaultFilters = { period: '1year', facility: '', sport: '', pitch: '', from_date: '', to_date: '' };
    dispatch(setFilters(defaultFilters));
    // Immediately fetch defaults to reflect clear on screen
    dispatch(fetchBookingDetails({ filters: clean(defaultFilters), pagination: { page: 1, limit: pagination.limit } }));
    setHasApplied(true);
  };

  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    dispatch(fetchBookingDetails({ filters: clean(filters), pagination: { page, limit: pagination.limit } }));
  };

  const handleExportExcel = async() => {
    if (!bookingDetails || bookingDetails.length === 0) return;

    const allBookingDetails = await getAllBookingDetails(clean(filters)) 
    // Adjust this based on your actual API response structure
    const allBookings = allBookingDetails?.data?.data || allBookingDetails?.data || [];
    
    // Prepare data for Excel export
    const excelData = allBookings.map(booking => ({
      'Booking ID': booking.bookingId || '-',
      'Booked By': booking.bookedBy || '-',
      'Date': booking.date || '-',
      'Time': booking.timing || '-',
      'Facility': booking.facilityName || '-',
      'Sport': booking.sportName || '-',
      'Pitch': booking.pitchName || '-',
      'Slots': booking.numberOfSlots || 0,
      'Revenue': booking.revenue || 0,
      'Payment Type': booking.paymentType || '-'
    }));

    // Convert to CSV format
    const headers = Object.keys(excelData[0]);
    const csvContent = [
      headers.join(','),
      ...excelData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `booking-details-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to sanitize text for PDF (remove problematic characters and fix spacing)
  const sanitizeTextForPDF = (text) => {
    if (!text || text === null || text === undefined) return '-';
    // Convert to string and handle any encoding issues
    let sanitized = String(text);
    
    // Remove control characters (0x00-0x1F, 0x7F-0x9F)
    sanitized = sanitized.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
    
    // Normalize whitespace - replace multiple spaces/newlines/tabs with single space
    sanitized = sanitized.replace(/\s+/g, ' ');
    
    // Remove any remaining problematic characters that might cause encoding issues
    // Keep standard printable ASCII (32-126) and common Unicode ranges
    sanitized = sanitized.replace(/[^\x20-\x7E\u00A0-\u00FF\u0100-\u017F]/g, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    // If the result is empty, return dash
    return sanitized || '-';
  };

  const handleExportPDF = async() => {
    if (!bookingDetails || bookingDetails.length === 0) return;

    const allBookingDetails = await getAllBookingDetails(clean(filters)) 
    // Adjust this based on your actual API response structure
    const allBookings = allBookingDetails?.data?.data || allBookingDetails?.data || [];
    
    // Create new PDF document
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Add title
    doc.setFontSize(20);
    doc.text('Booking Details Report', 14, 22);
    
    // Add generation date and total records
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
    doc.text(`Total Records: ${allBookings.length}`, 14, 35);
    
    // Format and display applied filters in row format
    let yPosition = 42;
    doc.setFontSize(10);
    
    // Format period filter
    let periodLabel = '';
    if (filters.period === 'custom') {
      const fromDate = filters.from_date ? toMMDDYYYY(filters.from_date) : 'Not set';
      const toDate = filters.to_date ? toMMDDYYYY(filters.to_date) : 'Not set';
      periodLabel = `Custom Period: ${fromDate} to ${toDate}`;
    } 
    else if (filters.period === '1year') {
      periodLabel = 'Time Period: 1 Year';
    } else {
      periodLabel = `Time Period: ${filters.period || 'Not set'}`;
    }
    
    // Format facility filter - sanitize the facility name
    const selectedFacilityObj = facilitiesFromFilters.find(f => f._id === filters.facility);
    const facilityName = filters.facility 
      ? sanitizeTextForPDF(selectedFacilityObj?.name || filters.facility)
      : 'All Facilities';
    const facilityLabel = `Facility: ${facilityName}`;
    
    // Format sport filter - sanitize the sport name
    const selectedSportObj = sports.find(s => s._id === filters.sport);
    const sportName = filters.sport 
      ? sanitizeTextForPDF(selectedSportObj?.name || filters.sport)
      : 'All Sports';
    const sportLabel = `Sport: ${sportName}`;
    
    // Format pitch filter - sanitize the pitch name
    const selectedPitchObj = filteredPitches.find(p => p._id === filters.pitch) || 
                             availablePitches.find(p => p._id === filters.pitch);
    const pitchName = filters.pitch 
      ? sanitizeTextForPDF(selectedPitchObj?.name || filters.pitch)
      : 'All Pitches';
    const pitchLabel = `Pitch/Court: ${pitchName}`;
    
    // Combine all filters in a single row with proper spacing - sanitize period label
    const sanitizedPeriodLabel = sanitizeTextForPDF(periodLabel);
    // const filtersText = `Applied Filters: ${sanitizedPeriodLabel}  |  ${facilityLabel}  |  ${sportLabel}  |  ${pitchLabel}`;
    const filtersText = `Applied Filters: ${sanitizedPeriodLabel}  |  ${facilityLabel}  |  ${sportLabel}  |  ${pitchLabel}`;

    
    doc.setFont(undefined, 'normal');
    doc.text(filtersText, 14, yPosition);
    yPosition += 7;
    
    // Prepare table data with sanitized text
    const tableData = allBookings.map(booking => [
      sanitizeTextForPDF(booking.bookingId),
      sanitizeTextForPDF(booking.bookedBy),
      sanitizeTextForPDF(booking.date),
      sanitizeTextForPDF(booking.timing),
      sanitizeTextForPDF(booking.facilityName), // Sanitize facility name to fix encoding issues
      sanitizeTextForPDF(booking.sportName),
      sanitizeTextForPDF(booking.pitchName),
      booking.numberOfSlots || 0,
      `PKR ${booking.revenue || 0}`,
      sanitizeTextForPDF(booking.paymentType)
    ]);
    
    // Define table columns
    const columns = [
      'Booking ID',
      'Booked By',
      'Date',
      'Time',
      'Facility',
      'Sport',
      'Pitch',
      'Slots',
      'Revenue',
      'Payment Type'
    ];
    
    // Calculate total revenue first
    const totalRevenue = allBookingDetails.data.data.reduce((sum, booking) => {
      return sum + (parseFloat(booking.revenue) || 0);
    }, 0);
    
    // Create footer row with total - empty cells except for "Total" label and revenue total
    const footerRow = [
      '', // Booking ID
      '', // Booked By
      '', // Date
      '', // Time
      '', // Facility
      '', // Sport
      '', // Pitch
      'Total', // Slots column - using it for "Total" label
      formatCurrency(totalRevenue), // Revenue column - total revenue
      '' // Payment Type
    ];
    
    // Add table to PDF with footer
    autoTable(doc, {
      head: [columns],
      body: tableData,
      foot: [footerRow],
      startY: yPosition + 3,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        font: 'helvetica',
      },
      headStyles: {
        fillColor: [56, 176, 0], // App primary color #38b000
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        font: 'helvetica'
      },
      footStyles: {
        fillColor: [56, 176, 0], // App primary color #38b000
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        font: 'helvetica'
      },
      columnStyles: {
        0: { font: 'helvetica', fontSize: 8 }, // Booking ID
        1: { font: 'helvetica', fontSize: 8 }, // Booked By
        2: { font: 'helvetica', fontSize: 8 }, // Date
        3: { font: 'helvetica', fontSize: 8 }, // Time
        4: { font: 'helvetica', fontSize: 8 }, // Facility - explicitly set
        5: { font: 'helvetica', fontSize: 8 }, // Sport
        6: { font: 'helvetica', fontSize: 8 }, // Pitch
        7: { font: 'helvetica', fontSize: 8 }, // Slots
        8: { font: 'helvetica', fontSize: 8 }, // Revenue
        9: { font: 'helvetica', fontSize: 8 }  // Payment Type
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
        font: 'helvetica',
        fontSize: 8
      },
      margin: { top: yPosition + 3 }
    });
    
    // Save the PDF
    doc.save(`booking-details-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Show error as a dismissible alert at the top if there's an error
  const ErrorAlert = () => {
    if (error === null) return null;

    return (
      <Alert variant="danger" dismissible onClose={() => dispatch(resetError())} className="mb-3">
        <Alert.Heading>Error Loading Analytics</Alert.Heading>
        <p>{error}</p>
        <hr />
        <p className="mb-0">
          Please check your network connection and try again. If the problem persists, contact support.
        </p>
      </Alert>
    );
  };

  return (
    <>
      <div className={getContainerClass(location.pathname)}>
        {/* <Header /> */}
        {/* <Cover breadcrumb={breadcrumb} page="reports" /> */}

        <Container fluid className="analytics-page">

          {/* Error Alert */}
          <ErrorAlert />

          {/* Filters Section */}
          {loading && !hasApplied ? (
            <SkeletonFilters />
          ) : (
            <Card className="filter-card mb-4">
              <Card.Body>
                {loading && (
                  <div className="filter-loading-overlay">
                    <div className="filter-loading-message">
                      <Spinner size="sm" className="me-2" />
                      Applying filters...
                    </div>
                  </div>
                )}
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Time Period</Form.Label>
                    <Form.Select
                      value={filters.period}
                      onChange={(e) => handleFilterChange('period', e.target.value)}
                    >
                      {periodOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  {filters.period === 'custom' && (
                    <>
                      <Col md={3}>
                        <Form.Label>From</Form.Label>
                        <Form.Control
                          type="date"
                          value={filters.from_date || ''}
                          onChange={(e) => handleFilterChange('from_date', e.target.value)}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>To</Form.Label>
                        <Form.Control
                          type="date"
                          value={filters.to_date || ''}
                          onChange={(e) => handleFilterChange('to_date', e.target.value)}
                        />
                      </Col>
                    </>
                  )}

                  <Col md={3}>
                    <Form.Label>Facility</Form.Label>
                    <Form.Select
                      value={filters.facility}
                      onChange={(e) => handleFilterChange('facility', e.target.value)}
                    >
                      <option value="">All Facilities</option>
                      {facilitiesFromFilters.map(facility => (
                        <option key={facility._id} value={facility._id}>
                          {facility.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={3}>
                    <Form.Label>Sport</Form.Label>
                    <Form.Select
                      value={filters.sport}
                      onChange={(e) => handleFilterChange('sport', e.target.value)}
                      disabled={!filters.facility}
                    >
                      <option value="">All Sports</option>
                      {filteredSports.map(sport => (
                        <option key={sport._id} value={sport._id}>
                          {sport.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>

                  <Col md={3}>
                    <Form.Label>Pitch/Court</Form.Label>
                    <Form.Select
                      value={filters.pitch}
                      onChange={(e) => handleFilterChange('pitch', e.target.value)}
                      disabled={!filters.facility || !filters.sport}
                    >
                      <option value="">All Pitches</option>
                      {filteredPitches.length === 0 && filters.facility && filters.sport ? (
                        <option value="" disabled>No pitches available</option>
                      ) : (
                        filteredPitches.map(pitch => (
                          <option key={pitch._id} value={pitch._id}>
                            {pitch.name}
                          </option>
                        ))
                      )}
                    </Form.Select>
                  </Col>

                  <Col md={3}>
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        onClick={handleApplyFilters}
                        disabled={loading || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                        className="flex-grow-1 btn-apply-analytics"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          'Apply Filters'
                        )}
                      </Button>
                      <Button
                        variant="outline-success"
                        onClick={handleRefresh}
                        disabled={loading || refreshing || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                        title="Refresh"
                        style={{ whiteSpace: 'nowrap', minWidth: 90 }}
                      >
                        {refreshing ? <Spinner size="sm" /> : 'Refresh'}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleClearFilters}
                        disabled={loading}
                        className="btn-clear"
                      >
                        Clear
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Booking Details Table */}
          {loading && !hasApplied ? (
            <SkeletonTable title="Booking Details Report" />
          ) : (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Booking Details Report</h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleExportExcel}
                    disabled={loading || !bookingDetails || bookingDetails.length === 0}
                  >
                    <i className="fa fa-file-excel me-1"></i>
                    Export Excel
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleExportPDF}
                    disabled={loading || !bookingDetails || bookingDetails.length === 0}
                  >
                    <i className="fa fa-file-pdf me-1"></i>
                    Export PDF
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading booking details...</p>
                  </div>
                ) : bookingDetails && bookingDetails.length > 0 ? (
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Booked By</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Facility</th>
                        <th>Sport</th>
                        <th>Pitch</th>
                        <th>Slots</th>
                        <th>Revenue</th>
                        <th>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingDetails.map((booking, index) => (
                        <tr key={index}>
                          <td>{booking.bookingId || '-'}</td>
                          <td>{booking.bookedBy || '-'}</td>
                          <td>{booking.date || '-'}</td>
                          <td>{booking.timing || '-'}</td>
                          <td>{booking.facilityName || '-'}</td>
                          <td>{booking.sportName || '-'}</td>
                          <td>{booking.pitchName || '-'}</td>
                          <td>{booking.numberOfSlots || 0}</td>
                          <td className="text-success fw-bold">{formatCurrency(booking.revenue || 0)}</td>
                          <td>
                            <Badge bg={booking.paymentType === 'cash' ? 'success' : 'primary'}>
                              {booking.paymentType || '-'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <div className="empty-state">
                      <div className="mb-4">
                        <i className="fa fa-table fa-3x text-muted mb-3"></i>
                      </div>
                      <h5 className="text-muted">No Booking Data Available</h5>
                      <p className="text-muted mb-4">No booking details found for the selected filters. Try adjusting your filter criteria and apply again.</p>
                      <Button
                        variant="outline-primary"
                        onClick={handleApplyFilters}
                        disabled={loading}
                      >
                        {loading ? <Spinner size="sm" className="me-2" /> : null}
                        Retry
                      </Button>
                    </div>
                  </div>
                )}

                {/* Pagination Controls */}
                {bookingDetails && bookingDetails.length > 0 && (
                  <Row className="mt-1">
                    <Col className="d-flex justify-content-center">
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${pagination.currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(pagination.totalPages)].map((_, index) => (
                            <li
                              key={index}
                              className={`page-item ${pagination.currentPage === index + 1 ? "active" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${pagination.currentPage === pagination.totalPages ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
    </>
  );
};

export default AnalyticsPage; 